// covered-by: services/christo/lib/services/__tests__/askPunttOpenItems.test.ts
// covered-by: qa/rickrubin/tests/api/ask-puntt-projects.spec.ts
import { z } from "zod";

export const askPunttOpenItemKindSchema = z.enum([
  "project_blocker",
  "missing_input",
  "vague_requirement",
  "unjudged_finding",
  "expert_question",
  "source_conflict",
]);
export type AskPunttOpenItemKind = z.infer<typeof askPunttOpenItemKindSchema>;

/**
 * One button on a conflict card (L2.20): keep the standing answer, or accept the
 * proposal. Typed by warhol when it files a resolution request; `answer` is for
 * display only, and the click sends `resolution`, never words.
 */
export const askPunttOpenItemResolutionOptionSchema = z.object({
  resolution: z.enum(["keep", "accept"]),
  answer: z.string().min(1).max(300),
});
export type AskPunttOpenItemResolutionOption = z.infer<
  typeof askPunttOpenItemResolutionOptionSchema
>;
export const askPunttOpenItemResolutionOptionsSchema = z
  .array(askPunttOpenItemResolutionOptionSchema)
  .max(2);

export const askPunttOpenItemSchema = z.object({
  id: z.string(),
  kind: askPunttOpenItemKindSchema,
  needed: z.string(),
  ownerUserId: z.string().nullable(),
  openedAt: z.union([z.string(), z.date()]),
  fileId: z.string().nullable(),
  fileName: z.string().nullable().optional(),
  sourceId: z.string(),
  sourceKind: z.enum([
    "attention",
    "finding",
    "participation",
    "approval",
    "conflict",
    "agent",
  ]),
  reviewTicketId: z.string().nullable().optional(),
  commentId: z.string().nullable().optional(),
  status: z.enum(["open", "cleared"]).optional(),
  suggestions: z.array(z.string()).optional(),
  resolutionOptions: askPunttOpenItemResolutionOptionsSchema.optional(),
  severity: z.string().optional(),
});
export type AskPunttOpenItem = z.infer<typeof askPunttOpenItemSchema>;

export const listAskPunttProjectOpenItemsRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
});
/** An item the viewer raised that an Expert settled: how, by whom, and when. */
export const askPunttSettledOpenItemSchema = z.object({
  id: z.string(),
  kind: askPunttOpenItemKindSchema,
  needed: z.string(),
  resolutionNote: z.string(),
  settledByUserId: z.string().nullable(),
  settledByName: z.string().nullable(),
  settledAt: z.union([z.string(), z.date()]),
});
export type AskPunttSettledOpenItem = z.infer<
  typeof askPunttSettledOpenItemSchema
>;

export const listAskPunttProjectOpenItemsResponseSchema = z.object({
  openItems: z.array(askPunttOpenItemSchema),
  /** Items the viewer raised in this project that closed in the last 14 days. */
  settled: z.array(askPunttSettledOpenItemSchema).optional(),
  doneAt: z.union([z.string(), z.date()]).nullable(),
  mayAssignOpenItems: z.boolean().optional(),
  canMarkProjectDone: z.boolean().optional(),
});
export type ListAskPunttProjectOpenItemsResponse = z.infer<
  typeof listAskPunttProjectOpenItemsResponseSchema
>;

/** Composer attachment for an open item on the project rail (Ask V2). */
export const askPunttOpenItemContextSchema = z.object({
  id: z.string().min(1),
  kind: askPunttOpenItemKindSchema,
  needed: z.string().min(1),
});
export type AskPunttOpenItemContext = z.infer<
  typeof askPunttOpenItemContextSchema
>;

export const askPunttOpenItemContextsSchema = z
  .array(askPunttOpenItemContextSchema)
  .max(1);

export const markAskPunttProjectDoneRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  body: z
    .object({
      override: z.boolean().optional(),
    })
    .default({}),
});
export const markAskPunttProjectDoneResponseSchema = z.object({
  done: z.literal(true),
  doneAt: z.union([z.string(), z.date()]),
  override: z.boolean(),
  items: z
    .array(
      z.object({
        id: z.string(),
        kind: askPunttOpenItemKindSchema,
        needed: z.string(),
        ownerUserId: z.string().nullable(),
        fileId: z.string().nullable(),
      }),
    )
    .optional(),
});

export const assignAskPunttOpenItemRequestSchema = z.object({
  params: z.object({ projectId: z.string(), itemId: z.string() }),
  body: z.object({ ownerUserId: z.string().min(1) }),
});

export const nudgeAskPunttOpenItemRequestSchema = z.object({
  params: z.object({ projectId: z.string(), itemId: z.string() }),
});

export const nudgeAskPunttOpenItemResponseSchema = z.object({
  nudged: z.literal(true),
  message: z.string(),
});

/** A conflict card's click: the typed option chosen, and why (L2.20). */
export const resolveAskPunttOpenItemRequestSchema = z.object({
  params: z.object({ projectId: z.string(), itemId: z.string() }),
  body: z.object({
    resolution: z.enum(["keep", "accept"]),
    reason: z.string().max(600).default(""),
  }),
});

/** What warhol's decision door did with the click. `pending` when it is still running. */
export const resolveAskPunttOpenItemResponseSchema = z.object({
  outcome: z.string(),
  check: z.string(),
  message: z.string(),
});
export type ResolveAskPunttOpenItemResponse = z.infer<
  typeof resolveAskPunttOpenItemResponseSchema
>;

export const listAskPunttOpenItemAssigneesResponseSchema = z.object({
  assignees: z.array(
    z.object({
      userId: z.string(),
      name: z.string(),
    }),
  ),
});
export type MarkAskPunttProjectDoneResponse = z.infer<
  typeof markAskPunttProjectDoneResponseSchema
>;

/** One open item waiting on the viewer, on the v2 home. */
export const askPunttForYouWaitingItemSchema = z.object({
  id: z.string(),
  kind: askPunttOpenItemKindSchema,
  needed: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  openedAt: z.union([z.string(), z.date()]),
  raisedByName: z.string().nullable(),
  raisedByRole: z.string().nullable(),
  resolutionOptions: askPunttOpenItemResolutionOptionsSchema.optional(),
  /** False when the viewer owns the item but may not read its project: the card names it and links nowhere. */
  projectVisible: z.boolean().optional(),
});
export type AskPunttForYouWaitingItem = z.infer<
  typeof askPunttForYouWaitingItemSchema
>;

/** One request the viewer raised that closed, on the v2 home. */
export const askPunttForYouRequestUpdateSchema =
  askPunttSettledOpenItemSchema.extend({
    projectId: z.string(),
    projectName: z.string(),
  });
export type AskPunttForYouRequestUpdate = z.infer<
  typeof askPunttForYouRequestUpdateSchema
>;

export const getAskPunttForYouResponseSchema = z.object({
  waitingOnYou: z.array(askPunttForYouWaitingItemSchema),
  requestUpdates: z.array(askPunttForYouRequestUpdateSchema),
});
export type GetAskPunttForYouResponse = z.infer<
  typeof getAskPunttForYouResponseSchema
>;
